// App.jsx - Updated with new theme and structure
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  ThemeProvider,
  createTheme,
  CssBaseline,
  Drawer,
  AppBar,
  Toolbar,
  IconButton,
  useMediaQuery,
  Switch,
  FormControlLabel,
  Button,
  Chip,
  Stack,
  Alert,
  Card,
  CardContent,
  Grid,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Brightness4,
  Brightness7,
  History,
  AccessTime,
  MailOutline,
  Visibility,
  Forum,
} from '@mui/icons-material';
import Sidebar from './components/Sidebar/Sidebar';
import SearchDashboard from './components/SearchDashboard/SearchDashboard';
import { useCarrierData } from './hooks/useCarrierData';
import { useAuth } from './context/AuthContext';
import AdminPortal from './components/AdminPortal/AdminPortal';
import LoginPanel from './components/Auth/LoginPanel';
import EmailWorkspace from './components/Email/EmailWorkspace';
import { adminApi } from './services/adminApi';
import './styles/globals.css';

const getDesignTokens = (mode) => ({
  palette: {
    mode,
    ...(mode === 'light'
      ? {
          // Light theme
          primary: {
            main: '#2563eb',
            light: '#3b82f6',
            dark: '#1d4ed8'
          },
          secondary: {
            main: '#059669',
            light: '#10b981',
            dark: '#047857'
          },
          background: {
            default: '#f8fafc',
            paper: '#ffffff',
          },
          text: {
            primary: '#1e293b',
            secondary: '#64748b',
          },
          grey: {
            50: '#f8fafc',
            100: '#f1f5f9',
            200: '#e2e8f0',
            300: '#cbd5e1',
            400: '#94a3b8',
            500: '#64748b',
            600: '#475569',
            700: '#334155',
            800: '#1e293b',
            900: '#0f172a',
          }
        }
      : {
          // Dark theme
          primary: {
            main: '#3b82f6',
            light: '#60a5fa',
            dark: '#2563eb'
          },
          secondary: {
            main: '#10b981',
            light: '#34d399',
            dark: '#059669'
          },
          background: {
            default: '#0f172a',
            paper: '#1e293b',
          },
          text: {
            primary: '#f1f5f9',
            secondary: '#cbd5e1',
          },
          grey: {
            50: '#f8fafc',
            100: '#f1f5f9',
            200: '#e2e8f0',
            300: '#cbd5e1',
            400: '#94a3b8',
            500: '#64748b',
            600: '#475569',
            700: '#334155',
            800: '#1e293b',
            900: '#0f172a',
          }
        }),
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h4: {
      fontWeight: 700,
    },
    h5: {
      fontWeight: 600,
    },
    h6: {
      fontWeight: 600,
    },
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
          border: '1px solid',
          borderColor: mode === 'light' ? '#e2e8f0' : '#334155',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
        },
      },
    },
  },
});

const drawerWidth = 280;

function App() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('search');
  const [searchHistory, setSearchHistory] = useState([]);
  const [darkMode, setDarkMode] = useState(false);
  const { isAuthenticated, user, logout, token, isLoading } = useAuth();
  const workTickRef = React.useRef(null);
  
  const prefersDarkMode = useMediaQuery('(prefers-color-scheme: dark)');
  
  useEffect(() => {
    setDarkMode(prefersDarkMode);
  }, [prefersDarkMode]);

  const theme = React.useMemo(() => 
    createTheme(getDesignTokens(darkMode ? 'dark' : 'light')),
    [darkMode]
  );

  const [activityStats, setActivityStats] = useState(null);

  useEffect(() => {
    let timer;
    const load = () => {
      if (!isAuthenticated || !token) {
        setActivityStats(null);
        return;
      }
      adminApi.getActivityStats(token)
        .then((res) => setActivityStats(res?.stats || null))
        .catch(() => {});
    };
    load();
    if (isAuthenticated && token) {
      timer = setInterval(load, 20000); // refresh every 20s
    }
    return () => {
      if (timer) clearInterval(timer);
    };
  }, [isAuthenticated, token]);

  // Log periodic work ticks to estimate hours
  useEffect(() => {
    if (!isAuthenticated || !token) {
      if (workTickRef.current) clearInterval(workTickRef.current);
      return;
    }
    const sendTick = () => {
      adminApi.logActivity({ type: 'work_tick', source: 'app', metadata: { minutes: 5 } }, token).catch(() => {});
    };
    sendTick();
    workTickRef.current = setInterval(sendTick, 5 * 60 * 1000);
    return () => {
      if (workTickRef.current) clearInterval(workTickRef.current);
    };
  }, [isAuthenticated, token]);

  // Activity metrics from API fallback to local history
  const todayKey = new Date().toDateString();
  const thisMonthKey = `${new Date().getFullYear()}-${new Date().getMonth()}`;
  const callsToday = activityStats ? activityStats.call?.today || 0 : searchHistory.filter((s) => new Date(s.timestamp).toDateString() === todayKey).length;
  const callsMonth = activityStats ? activityStats.call?.month || 0 : searchHistory.filter((s) => {
    const d = new Date(s.timestamp);
    return `${d.getFullYear()}-${d.getMonth()}` === thisMonthKey;
  }).length;
  const emailSentToday = activityStats ? activityStats.email_send?.today || 0 : 0;
  const emailSentMonth = activityStats ? activityStats.email_send?.month || 0 : 0;
  const inboxRepliesToday = activityStats ? activityStats.email_reply?.today || 0 : 0;
  const inboxRepliesMonth = activityStats ? activityStats.email_reply?.month || 0 : 0;
  const hoursWorkedToday = activityStats ? (activityStats.work_minutes?.today || 0) / 60 : 0;
  const hoursWorkedMonth = activityStats ? (activityStats.work_minutes?.month || 0) / 60 : 0;

  const { carrierData, loading, error, searchCarrier, batchSearch, clearBatchResults, showBasicInfo, toggleInfoView, usageRemaining } = useCarrierData();
  
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleSearch = (term, type, filters = {}) => {
    const defaultFilters = {
      skipBrokers: false,
      skipInactive: false,
      showInactiveOnly: false,
      activeDuration: 0,
      startNumber: '',
      endNumber: '',
      resultLimit: 10
    };
    
    const finalFilters = { ...defaultFilters, ...filters };
    
    searchCarrier(term, type, finalFilters);
    
    const newSearch = {
      term,
      type,
      timestamp: new Date().toLocaleString(),
      result: carrierData ? 'Found' : 'Not Found',
      filters: finalFilters
    };
    
    setSearchHistory(prev => [newSearch, ...prev.slice(0, 9)]);
  };

  const handleBatchSearch = (batchInput, filters = {}) => {
    const searchTerms = batchInput.split('\n').filter(term => term.trim());
    
    if (searchTerms.length > 0) {
      batchSearch(batchInput, filters);
    }
  };

  const toggleDarkMode = () => {
    setDarkMode(!darkMode);
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box sx={{ display: 'flex' }}>
        {/* App Bar - Hidden when Email Studio is active */}
        {activeTab !== 'email' && (
          <AppBar
            position="fixed"
            sx={{
              width: { md: `calc(100% - ${drawerWidth}px)` },
              ml: { md: `${drawerWidth}px` },
              backgroundColor: 'background.paper',
              color: 'text.primary',
              boxShadow: 1,
              borderBottom: '1px solid',
              borderColor: 'divider'
            }}
          >
            <Toolbar>
              <IconButton
                color="inherit"
                aria-label="open drawer"
                edge="start"
                onClick={handleDrawerToggle}
                sx={{ mr: 2, display: { md: 'none' } }}
              >
                <MenuIcon />
              </IconButton>
              
              <Typography variant="h6" component="div" sx={{ flexGrow: 1, fontWeight: 600 }}>
                <Typography variant="body2" color="text.secondary">
                  {activeTab === 'search'
                    ? 'Carrier Search'
                    : activeTab === 'history'
                      ? 'Search History'
                      : activeTab === 'activity'
                        ? 'Activity'
                        : 'Admin Portal'}
                </Typography>
              </Typography>
              {typeof usageRemaining === 'number' && activeTab !== 'email' && (
                <Chip
                  label={`Remaining: ${usageRemaining}`}
                  color={usageRemaining > 0 ? 'success' : 'warning'}
                  sx={{ mr: 2, display: { xs: 'none', sm: 'inline-flex' } }}
                />
              )}
            </Toolbar>
          </AppBar>
        )}

        {/* Mobile Hamburger Button - Always visible on mobile */}
        <IconButton
          color="inherit"
          aria-label="open drawer"
          onClick={handleDrawerToggle}
          sx={{
            position: 'fixed',
            top: 16,
            left: 16,
            zIndex: 1200,
            display: { xs: 'flex', md: 'none' },
            backgroundColor: 'primary.main',
            color: 'white',
            '&:hover': {
              backgroundColor: 'primary.dark',
            }
          }}
        >
          <MenuIcon />
        </IconButton>

        {/* Sidebar */}
        <Sidebar
          drawerWidth={drawerWidth}
          mobileOpen={mobileOpen}
          handleDrawerToggle={handleDrawerToggle}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          searchHistory={searchHistory}
          onToggleDarkMode={toggleDarkMode}
          darkMode={darkMode}
          isAdminLoggedIn={isAuthenticated}
          user={user}
          logout={logout}
          usageRemaining={usageRemaining}
        />

        {/* Main Content */}
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: activeTab === 'email' ? 0 : 3,
            width: { md: `calc(100% - ${drawerWidth}px)`, xs: '100%' },
            minHeight: '100vh',
            backgroundColor: 'background.default',
          }}
        >
          {activeTab !== 'email' && <Toolbar />}

          {isLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
              <Typography variant="h6" color="text.secondary">
                Loading session...
              </Typography>
            </Box>
          )}

          {!isLoading && (
            <>
              {/* SEARCH TAB */}
              {activeTab === 'search' && (
                isAuthenticated ? (
                  <>
                    {typeof usageRemaining === 'number' && (
                      <Alert
                        severity={usageRemaining > 0 ? 'info' : 'warning'}
                        sx={{ mb: 3 }}
                      >
                        {usageRemaining > 0
                          ? `You have ${usageRemaining} searches remaining today.`
                          : 'You have reached your daily search limit.'}
                      </Alert>
                    )}
                    <SearchDashboard
                      onSearch={handleSearch}
                      onBatchSearch={handleBatchSearch}
                      clearBatchResults={clearBatchResults}
                      loading={loading}
                      error={error}
                      carrierData={carrierData}
                      showBasicInfo={showBasicInfo}
                      onToggleView={toggleInfoView}
                      darkMode={darkMode}
                    />
                  </>
                ) : (
                  <Box sx={{ maxWidth: 600, mx: 'auto', mt: 8 }}>
                    <Typography
                      variant="h4"
                      textAlign="center"
                      fontWeight={700}
                      gutterBottom
                    >
                      Sign In Required
                    </Typography>
                    <Typography
                      variant="body1"
                      textAlign="center"
                      color="text.secondary"
                    >
                      Please log in to run carrier searches and track your daily
                      quota.
                    </Typography>
                    <LoginPanel />
                  </Box>
                )
              )}

              {/* HISTORY TAB */}
              {activeTab === 'history' && (
            <Box sx={{ maxWidth: '1200px', margin: '0 auto' }}>
              <Typography variant="h4" gutterBottom sx={{ 
                fontWeight: 700,
                color: 'text.primary',
                mb: 4
              }}>
                Search History
              </Typography>
              
              {searchHistory.length === 0 ? (
                <Box sx={{ 
                  textAlign: 'center', 
                  py: 8,
                  backgroundColor: 'background.paper',
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider'
                }}>
                  <History sx={{ fontSize: 64, color: 'text.secondary', mb: 2, opacity: 0.5 }} />
                  <Typography variant="h6" color="text.secondary">
                    No search history yet
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Your search queries will appear here
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ display: 'grid', gap: 2 }}>
                  {searchHistory.map((search, index) => (
                    <Box
                      key={index}
                      sx={{
                        p: 3,
                        backgroundColor: 'background.paper',
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'divider',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          backgroundColor: darkMode ? 'grey.800' : 'grey.50',
                          transform: 'translateY(-1px)',
                          boxShadow: '0 4px 12px 0 rgba(0, 0, 0, 0.05)'
                        }
                      }}
                      onClick={() => handleSearch(search.term, search.type)}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 1 }}>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          {search.term}
                        </Typography>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            backgroundColor: 
                              search.type === 'mc' ? 'primary.main' : 
                              search.type === 'dot' ? 'secondary.main' : 'warning.main',
                            color: 'white',
                            px: 1.5,
                            py: 0.5,
                            borderRadius: 1,
                            fontSize: '0.75rem',
                            fontWeight: 600
                          }}
                        >
                          {search.type.toUpperCase()}
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        Searched on {search.timestamp}
                      </Typography>
                      {search.filters && Object.values(search.filters).some(val => val) && (
                        <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                          {search.filters.skipBrokers && (
                            <Typography variant="caption" sx={{ 
                              backgroundColor: darkMode ? 'grey.700' : 'grey.200',
                              px: 1,
                              py: 0.5,
                              borderRadius: 1
                            }}>
                              No Brokers
                            </Typography>
                          )}
                          {search.filters.skipInactive && (
                            <Typography variant="caption" sx={{ 
                              backgroundColor: darkMode ? 'grey.700' : 'grey.200',
                              px: 1,
                              py: 0.5,
                              borderRadius: 1
                            }}>
                              Active Only
                            </Typography>
                          )}
                        </Box>
                      )}
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          )}

          {activeTab === 'activity' && (
            <Box sx={{ maxWidth: '1200px', margin: '0 auto' }}>
              <Typography variant="h4" gutterBottom sx={{ fontWeight: 700, mb: 2 }}>
                User Activity
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Daily and monthly activity. Stored server-side; edit/delete is disabled to preserve audit trail.
              </Typography>

              <Grid container spacing={2}>
                {[
                  { label: 'Calls Today', value: callsToday, icon: <History />, color: 'primary' },
                  { label: 'Calls This Month', value: callsMonth, icon: <History />, color: 'secondary' },
                  { label: 'Emails Sent (Today)', value: emailSentToday, icon: <MailOutline />, color: 'primary' },
                  { label: 'Emails Sent (Month)', value: emailSentMonth, icon: <MailOutline />, color: 'secondary' },
                  { label: 'Inbox Replies (Today)', value: inboxRepliesToday, icon: <Forum />, color: 'primary' },
                  { label: 'Inbox Replies (Month)', value: inboxRepliesMonth, icon: <Forum />, color: 'secondary' },
                  { label: 'Hours Worked (Today)', value: hoursWorkedToday, icon: <AccessTime />, color: 'primary' },
                  { label: 'Hours Worked (Month)', value: hoursWorkedMonth, icon: <AccessTime />, color: 'secondary' },
                ].map((card, idx) => (
                  <Grid item xs={12} sm={6} md={3} key={idx}>
                    <Card variant="outlined">
                      <CardContent>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Box sx={{ color: `${card.color}.main` }}>{card.icon}</Box>
                          <Typography variant="subtitle2" color="text.secondary">
                            {card.label}
                          </Typography>
                        </Stack>
                        <Typography variant="h5" fontWeight={700} sx={{ mt: 1 }}>
                          {card.value}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>

              <Card variant="outlined" sx={{ mt: 3 }}>
                <CardContent>
                  <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                    Timeline (snapshot)
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Detailed logging and charts will appear here once the backend metrics are wired. Data retention prevents edits/deletes.
                  </Typography>
                </CardContent>
              </Card>
            </Box>
          )}

          {activeTab === 'email' && (
            isAuthenticated ? (
              <Box sx={{ height: '100%', width: '100%', overflow: 'hidden' }}>
                <EmailWorkspace carrierData={carrierData} />
              </Box>
            ) : (
              <Box sx={{ maxWidth: 600, mx: 'auto', mt: 8 }}>
                <Typography
                  variant="h4"
                  textAlign="center"
                  fontWeight={700}
                  gutterBottom
                >
                  Email workspace requires login
                </Typography>
                <Typography
                  variant="body1"
                  textAlign="center"
                  color="text.secondary"
                  sx={{ mb: 3 }}
                >
                  Connect an account and send messages once you are signed in.
                </Typography>
                <LoginPanel />
              </Box>
            )
          )}

              {/* ADMIN PORTAL TAB */}
              {activeTab === 'admin' && (
                <AdminPortal />
              )}
            </>
          )}
        </Box>
      </Box>
    </ThemeProvider>
  );
}

export default App;
